import { useCreateCircleMutation } from "@/api/circles-api";
import { UploadIcon } from "@/assets/icons/UploadIcon";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { StepFooter } from "./stepFooter";
import { StepHeader } from "./stepHeader";
import { parseAPIError } from "@/utils/helpers";
import { useTour } from "@/hooks/useTour";

type CircleFormProps = {
  currentStep: number;
  setCurrentStep: (step: number) => void;
  setImage: (image: File | null) => void;
  setTitle: (title: string) => void;
  setCircleId: (circleId: string) => void;
  image: File | null;
  onCircleCreated?: (circleId: string, title: string, image: File | null) => void;
};

const CircleForm = ({
  currentStep,
  setCurrentStep,
  setImage,
  setTitle,
  setCircleId,
  image,
  onCircleCreated,
}: CircleFormProps) => {
  const { t } = useTranslation();
  const { tourState, setFormValidation } = useTour();
  const nextStep = () => setCurrentStep(currentStep + 1);
  const [previewImage, setPreviewImage] = useState<boolean>(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const createCircleMutation = useCreateCircleMutation();

  useEffect(() => {
    if (image) {
      const url = URL.createObjectURL(image);
      setImagePreviewUrl(url);

      return () => URL.revokeObjectURL(url);
    }
  }, [image]);

  const SUPPORTED_FORMATS = ["image/jpeg", "image/png", "image/webp"];

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>, setFieldValue: any) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    const MAX_FILE_SIZE = 5 * 1024 * 1024;

    if (file.size > MAX_FILE_SIZE) {
      toast.error(t("profile.maxFileSize"), {
        position: "top-center",
      });
      return;
    }

    if (!SUPPORTED_FORMATS.includes(file.type)) {
      toast.error(t("profile.invalidFileType"), {
        position: "bottom-center",
      });
      return;
    }

    setFieldValue("image", file);
    setImage(file);
    setPreviewImage(true);
  };
  function handleRemoveImage(setFieldValue: (field: string, value: any) => void) {
    setFieldValue("image", null);
    setImage(null);
    setPreviewImage(false);
  }

  return (
    <Formik
      initialValues={{ image: null, title: "", description: "" }}
      validationSchema={Yup.object().shape({
        title: Yup.string().required("Name is required"),
        description: Yup.string().required("Description is required"),
      })}
      onSubmit={async (values) => {
        // Always use real API call - no more tour mode mocking
        createCircleMutation.mutate(
          {
            title: values.title,
            description: values.description,
            cover_photo: values.image ?? undefined,
          },
          {
            onSuccess: (response) => {
              queryClient.invalidateQueries({ queryKey: ["circles"] });
              setCircleId(response.data.id);
              if (onCircleCreated) {
                onCircleCreated(response.data.id, values.title, values.image);
              } else {
                nextStep();
              }
            },
            onError: (error) => {
              const errors = parseAPIError(error);
              errors.forEach((error) => {
                toast.error(error);
              });
            },
          },
        );
      }}
    >
      {({ handleSubmit, setFieldValue, errors, touched, values }) => {
        // Update tour context with form validation state
        useEffect(() => {
          if (tourState.isActive && tourState.tourType === "create-circle") {
            const titleValid = values.title.trim().length > 0;
            const descriptionValid = values.description.trim().length > 0;
            const isFormValid = titleValid && descriptionValid;

            setFormValidation({
              titleValid,
              descriptionValid,
              isFormValid,
            });
          }
        }, [values.title, values.description, tourState.isActive, tourState.tourType]);

        return (
          <>
            <StepHeader currentStep={currentStep} setCurrentStep={setCurrentStep} />
            <div
              id="joyride-circle-form"
              className="scrollbar-hide mt-2 flex h-screen w-full flex-col gap-8 overflow-y-auto bg-background pt-4 md:mb-[90px] md:pt-16 lg:max-w-[615px]"
            >
              <h1 className=" text-xl font-bold md:text-3xl">{t("circle.title")}</h1>
              <Form action="post" data-tour="circle-form-fields">
                <label htmlFor="circle-name" className="text-xs md:text-base">
                  {t("circle.circleName")}
                </label>
                <input
                  type="text"
                  className="mt-2 min-h-16 w-full rounded-lg border border-[#CCC2BF] bg-[#EDEAE4] p-2 text-sm font-bold text-[#232329] outline-none placeholder:font-light placeholder:text-[#4F4754]
              md:text-base"
                  placeholder={t("circle.firstInputPlaceholder")}
                  id="circle-name"
                  name="title"
                  onChange={(e) => {
                    setFieldValue("title", e.target.value);
                    setTitle(e.target.value);
                  }}
                />
                {errors.title && touched.title && (
                  <div className="ms-2 mt-2 text-sm text-red-500">{t("circle.requiredName")}</div>
                )}
                <div className="mt-8">
                  <label htmlFor="circle-description" className="text-xs md:text-base">
                    {t("circle.circleDescription")}
                  </label>
                  <div className="mt-2 flex w-full flex-col gap-4 rounded-lg border border-[#CCC2BF] bg-background px-4 py-2 text-base text-[#232329] md:gap-8">
                    <textarea
                      id="challengeDescription"
                      value={values.description}
                      onChange={(e) => {
                        setFieldValue("description", e.target.value);
                      }}
                      placeholder={t("challenge.describePlaceholder")}
                      className="flex min-h-[70px] w-full resize-none bg-transparent text-sm font-bold ring-offset-background placeholder:font-[350] placeholder:text-[#4F4754] placeholder:text-muted-foreground focus:text-[#232329] focus-visible:outline-none md:text-base"
                      rows={Math.max(2, Math.ceil(values.description.length / 60))}
                      maxLength={600}
                      name="description"
                    />
                    <span className="text-end text-xs font-[350] text-[#AC9CA0] md:text-sm">
                      <Trans
                        i18nKey={"challenge.characters"}
                        values={{ top: values.description.length, bottom: 600 }}
                      />
                    </span>
                  </div>
                  {errors.description && touched.description && (
                    <div className="ms-2 mt-2 text-sm text-red-500">
                      {t("circle.requiredDescription")}
                    </div>
                  )}
                </div>
              </Form>

              {previewImage ? (
                <div className="relative h-32 w-full ">
                  <img
                    src={imagePreviewUrl || ""}
                    alt=""
                    className=" h-full w-full rounded-lg object-cover "
                  />
                  <button
                    className="absolute left-[40%] top-1/2 rounded-lg bg-[#EDEAE4] px-6 py-2 text-[#232329]"
                    onClick={() => handleRemoveImage(setFieldValue)}
                  >
                    Remove X
                  </button>
                </div>
              ) : (
                <Form className="relative" data-tour="circle-image-upload">
                  <div className="my-6 flex h-11 max-w-48 flex-col gap-4">
                    <label className=" flex h-11 w-full cursor-pointer items-center gap-1.5 rounded-lg bg-[#232329] px-6 py-2 font-semibold text-[#EDEAE4]">
                      {t("circle.uploadCover")}
                      <UploadIcon />
                      <input
                        type="file"
                        name="uploadFile"
                        aria-label="uploadFile"
                        accept="image/*"
                        title=""
                        onChange={(e) => {
                          handleUpload(e, setFieldValue);
                        }}
                        className="w-0 opacity-0 "
                      />
                    </label>
                  </div>
                </Form>
              )}
            </div>

            <StepFooter
              onSubmit={handleSubmit}
              currentStep={currentStep}
              isLoading={createCircleMutation.isPending}
            />
          </>
        );
      }}
    </Formik>
  );
};

export default CircleForm;
